import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Segmented, Skeleton, message } from "antd";
import { DownloadOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useExportPDF } from "util/exportPDF";
import {
  fetchIntentInsights,
  formatComputedAt,
  refreshIntentInsights,
} from "./intentInsightsService";
import { exportIntentInsightsCsv } from "./intentInsightsExport";
import { trackProspectProductMilestone } from "util/prospectTrialAnalytics";
import AutoInsightHero from "./AutoInsightHero";
import InsightsKpiRow from "./InsightsKpiRow";
import TopIntentsCard from "./TopIntentsCard";
import SentimentTrendCard from "./SentimentTrendCard";
import FrictionPointsCard from "./FrictionPointsCard";
import ObjectionLeaderboard from "./ObjectionLeaderboard";
import SalesHandoffRulesPanel from "./SalesHandoffRulesPanel";
import styles from "./intentInsights.module.scss";

export default function IntentInsightsTab({
  botId,
  botName,
  onViewAllChats,
}) {
  const { t, i18n } = useTranslation();
  const [periodDays, setPeriodDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [payload, setPayload] = useState(null);
  const printRef = useRef(null);
  const exportPdf = useExportPDF(printRef, {
    documentTitle: `Intent-Insights-${botName || botId || "bot"}-${periodDays}d`,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetchIntentInsights(botId, { periodDays });
      setPayload(res);
      if (res) {
        trackProspectProductMilestone("first_intent_insight", { bot_id: botId });
      }
    } catch (_) {
      setError(true);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [botId, periodDays]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefreshNow = async () => {
    setRefreshing(true);
    try {
      const res = await refreshIntentInsights(botId, { periodDays });
      setPayload(res);
      message.success(t("sonic_prospect.intent_insights.refresh_success"));
    } catch (_) {
      message.error(t("sonic_prospect.intent_insights.refresh_error"));
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = ({ key }) => {
    const data = payload?.data;
    if (!data) return;
    if (key === "csv") {
      exportIntentInsightsCsv({
        data,
        botName,
        periodDays,
        t,
      });
      message.success(t("sonic_prospect.intent_insights.export_csv_success"));
      return;
    }
    if (key === "pdf") {
      exportPdf();
    }
  };

  if (loading && !payload) {
    return (
      <div className={styles.insightsPanel}>
        <p className={styles.loadingLead}>{t("sonic_prospect.intent_insights.loading_title")}</p>
        <p className={styles.loadingSub}>{t("sonic_prospect.intent_insights.loading_sub")}</p>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.insightsState}>
        <h2>{t("sonic_prospect.intent_insights.error_title")}</h2>
        <p>{t("sonic_prospect.intent_insights.error_body")}</p>
        <Button type="primary" icon={<ReloadOutlined />} onClick={load}>
          {t("sonic_prospect.intent_insights.try_again")}
        </Button>
      </div>
    );
  }

  if (payload?.status === "empty") {
    const min = payload.minConversations ?? 10;
    const count = payload.conversationCount ?? 0;
    return (
      <div className={styles.insightsState}>
        <h2>{t("sonic_prospect.intent_insights.empty_title")}</h2>
        <p className={styles.emptyProgress}>
          {t("sonic_prospect.intent_insights.empty_progress", { count, min })}
        </p>
        <p>{t("sonic_prospect.intent_insights.empty_body")}</p>
        <p className={styles.emptyHint}>
          {t("sonic_prospect.intent_insights.empty_hint", { min, count })}
        </p>
        {payload.nextRefreshLabel ? (
          <p className={styles.emptyHint}>
            {t("sonic_prospect.intent_insights.empty_refresh_schedule", {
              schedule: payload.nextRefreshLabel,
            })}
          </p>
        ) : null}
        <Button type="primary" onClick={onViewAllChats}>
          {t("sonic_prospect.intent_insights.view_all_chats")}
        </Button>
      </div>
    );
  }

  if (payload?.status === "pending") {
    const min = payload.minConversations ?? 10;
    const count = payload.conversationCount ?? 0;
    return (
      <div className={styles.insightsState}>
        <h2>{t("sonic_prospect.intent_insights.pending_title")}</h2>
        <p className={styles.emptyProgress}>
          {t("sonic_prospect.intent_insights.pending_progress", { count, min })}
        </p>
        <p>{t("sonic_prospect.intent_insights.pending_body")}</p>
        {payload.pendingEod ? (
          <p className={styles.emptyHint}>
            {t("sonic_prospect.intent_insights.pending_eod", {
              schedule: payload.nextRefreshLabel,
            })}
          </p>
        ) : null}
        <Button type="primary" onClick={onViewAllChats}>
          {t("sonic_prospect.intent_insights.view_all_chats")}
        </Button>
      </div>
    );
  }

  const data = payload?.data;
  if (!data) return null;

  const lastUpdated = formatComputedAt(payload.computedAt, i18n.language);

  return (
    <div className={styles.insightsPanel} ref={printRef}>
      {payload.pendingEod ? (
        <p className={styles.pendingBanner}>
          {t("sonic_prospect.intent_insights.stale_banner", {
            schedule: payload.nextRefreshLabel,
          })}
        </p>
      ) : null}
      <div className={`${styles.insightsToolbar} no-print`}>
        <div className={styles.toolbarMeta}>
          <span className={styles.periodLabel}>{data.period?.label}</span>
          {lastUpdated ? (
            <span className={styles.lastUpdated}>
              {t("sonic_prospect.intent_insights.last_updated", { date: lastUpdated })}
            </span>
          ) : null}
        </div>
        <Segmented
          size="small"
          value={periodDays}
          onChange={setPeriodDays}
          options={[
            { label: t("sonic_prospect.intent_insights.period_7"), value: 7 },
            { label: t("sonic_prospect.intent_insights.period_30"), value: 30 },
          ]}
        />
        <Button
          icon={<SyncOutlined spin={refreshing} />}
          loading={refreshing}
          onClick={handleRefreshNow}
        >
          {t("sonic_prospect.intent_insights.refresh_now")}
        </Button>
        <Dropdown
          menu={{
            items: [
              { key: "csv", label: "CSV" },
              { key: "pdf", label: "PDF" },
            ],
            onClick: handleExport,
          }}
        >
          <Button icon={<DownloadOutlined />}>
            {t("sonic_prospect.intent_insights.export_report")}
          </Button>
        </Dropdown>
      </div>

      <AutoInsightHero insights={data.autoInsights} multiple />

      <InsightsKpiRow data={data} />

      <div className={styles.insightsGrid}>
        <div className={styles.insightsMainCol}>
          <TopIntentsCard intents={data.topIntents} />
          <ObjectionLeaderboard objections={data.objections} />
        </div>

        <div className={styles.insightsSideCol}>
          <SentimentTrendCard
            series={data.sentimentTrend}
            periodDays={periodDays}
            onPeriodChange={setPeriodDays}
          />
          <FrictionPointsCard points={data.frictionPoints} />
          <SalesHandoffRulesPanel botId={botId} />
        </div>
      </div>
    </div>
  );
}
